/* eslint-disable camelcase */
import { LandingLayoutWithoutSidebar } from "PFApp/components/landing_layout";
import { useOnboarding } from "PFApp/onboarding/use_onboarding";
import { Button } from "PFComponents/button";
import AutoSelect from "PFComponents/select/autoselect";
import InputFieldSet from "PFComponents/text/input_field_set";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { fetchAutocompleteCustomValues } from "PFCore/services/autocomplete/fetch_autocomplete_custom_values";
import { CurrentProfile, Position } from "PFTypes";
import React, { useMemo, useState } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import OnboardingBox from "../parts/onboarding_box";
import OnboardingHeader from "../parts/onboarding_header";
import css from "./common.less";

const ALLOWS_NEW_VALUES = ["allowed", "require_approval"];

const DetailsStep = () => {
  const { ready, stepDotsProps, currentProfile, suggestedProfile, customTypes, saveProfile, navigateNext } =
    useOnboarding("details");
  const { t } = useTranslation(["onboarding", "translation"]);

  const [errors, setErrors] = useState<Partial<CurrentProfile>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<Partial<CurrentProfile>>({});
  const [jobTitle, setJobTitle] = useState("");

  const { getFormattedErrors } = useResponseErrors();

  const jobTitleType = customTypes.find(
    ({ sections }) => !!sections.find(({ key }) => key === "current_experience_title_1")
  );

  const companyType = customTypes.find(
    ({ sections }) => !!sections.find(({ key }) => key === "current_experience_title_2")
  );

  const mainPosition = suggestedProfile?.positions?.find((position) => position.main);

  useEffect(() => {
    if (suggestedProfile) {
      // only operate on as little data as possible so we don't end up blocked by some random permission
      setData({
        first_name: suggestedProfile.first_name,
        last_name: suggestedProfile.last_name,
        summary: suggestedProfile.summary
      });
      const jobTitleValues = mainPosition && jobTitleType?.name ? mainPosition[jobTitleType.name] : null;
      setJobTitle(jobTitleValues ? jobTitleValues[0].value : "");
    }
  }, [!!suggestedProfile]);

  const canViewProfileField = (field: string): boolean => {
    const group = currentProfile.permissions_group;
    try {
      return (
        group.cross_group_permissions[group.id].profile_fields_access_levels[field] === "rw" ||
        group.cross_group_permissions[group.id].profile_fields_access_levels[field] === "ro"
      );
    } catch {
      return false;
    }
  };

  const canEditProfileField = (field: string): boolean => {
    const group = currentProfile.permissions_group;
    try {
      return group.cross_group_permissions[group.id].profile_fields_access_levels[field] === "rw";
    } catch {
      return false;
    }
  };

  const canUpdateFirstAndLastName = () => {
    const group = currentProfile.permissions_group;
    return ["ro", "rw"].includes(
      group.cross_group_permissions[group.id].profile_fields_access_levels["first_and_last_name"]
    );
  };

  const permissionGroup = currentProfile?.permissions_group;
  const crossGroupPermission = permissionGroup?.cross_group_permissions[permissionGroup.id];

  const jobTitleFieldName = `main_position/${jobTitleType?.name}`;
  const companyFieldName = `main_position/${companyType?.name}`;
  const summaryFieldName = `main_position/summary`;
  const datesFieldName = `main_position/dates`;

  const canEditJobTitle = crossGroupPermission
    ? crossGroupPermission.profile_fields_access_levels[jobTitleFieldName] === "rw" &&
      crossGroupPermission.profile_fields_access_levels[companyFieldName] === "rw" &&
      crossGroupPermission.profile_fields_access_levels[summaryFieldName] === "rw" &&
      crossGroupPermission.profile_fields_access_levels[datesFieldName] === "rw" &&
      !jobTitleType?.sections.find(({ key }) => key === "editable_only_by_admins")
    : false;

  const positionsToSave = useMemo(
    () =>
      jobTitleType?.name
        ? [{ ...(mainPosition || []), [jobTitleType.name]: { value: jobTitle } }]
        : [{ ...(mainPosition || []) }],
    [jobTitle, jobTitleType?.name, mainPosition]
  );

  const next = () => {
    setIsLoading(true);

    const dataToSave: Partial<CurrentProfile> & { registration_status: string } = {
      registration_status: "details",
      ...data,
      positions: positionsToSave as Position[]
    };

    if (!canEditProfileField("summary")) {
      delete dataToSave.summary;
    }

    if (!canUpdateFirstAndLastName()) {
      delete dataToSave.first_name;
      delete dataToSave.last_name;
    }

    if (!canEditJobTitle) {
      delete dataToSave.positions;
    }

    saveProfile(dataToSave)
      .then(navigateNext)
      .catch((response) => {
        setIsLoading(false);
        setErrors(getFormattedErrors(response));
      });
  };

  if (!ready) {
    return <LandingLayoutWithoutSidebar />; // avoid flicker
  }

  return (
    <LandingLayoutWithoutSidebar alignTop>
      <OnboardingBox qaId="Onboarding.DetailsStep" stepDotsProps={stepDotsProps} isLoading={isLoading}>
        <OnboardingHeader title={t("onboarding:profileDetails")} />
        <div className={css.inputsRow}>
          <InputFieldSet
            qaId="Onboarding.DetailsStep.first_name"
            controlledValue
            style={{ margin: "0 10px 20px 10px", flexGrow: 1 }}
            label={t("onboarding:profile.firstName")}
            locked={!canEditProfileField("first_and_last_name")}
            lockedTip={t("onboarding:profile.lockedTip")}
            name="first_name"
            value={data.first_name || ""}
            handleChange={(first_name) => setData({ ...data, first_name })}
            error={errors?.first_name}
          />

          <InputFieldSet
            qaId="Onboarding.DetailsStep.last_name"
            controlledValue
            style={{ margin: "0 10px 20px 10px", flexGrow: 1 }}
            label={t("onboarding:profile.lastName")}
            name="last_name"
            locked={!canEditProfileField("first_and_last_name")}
            lockedTip={t("onboarding:profile.lockedTip")}
            value={data.last_name || ""}
            handleChange={(last_name) => setData({ ...data, last_name })}
            error={errors?.last_name}
          />
        </div>

        {canViewProfileField("summary") && (
          <InputFieldSet
            qaId="Onboarding.DetailsStep.summary"
            controlledValue
            style={{ marginBottom: 20 }}
            label={t("onboarding:profile.summary")}
            name="summary"
            placeholder={t("onboarding:profile.summaryPlaceholder")}
            inputType="textarea"
            locked={!canEditProfileField("summary")}
            lockedTip={t("onboarding:profile.lockedTip")}
            tip={t("onboarding:profile.summaryTip")}
            maxLength={1000}
            value={data.summary || ""}
            handleChange={(summary) => setData({ ...data, summary })}
            minHeight={80}
            error={errors?.summary}
          />
        )}

        {jobTitleType && (
          <AutoSelect
            qaId="Onboarding.DetailsStep.job_title"
            key={jobTitle?.length}
            label={jobTitleType.display_as || t("onboarding:profile.jobTitle")}
            locked={!canEditJobTitle}
            lockedTip={t("onboarding:profile.lockedTip")}
            query={(term) => fetchAutocompleteCustomValues({ term, type: jobTitleType.name })}
            name="job_title"
            letCreate={jobTitleType.new_values && ALLOWS_NEW_VALUES.includes(jobTitleType.new_values)}
            values={jobTitle ? [{ text: jobTitle }] : []}
            closeOnChange
            handleChange={(jobTitle) => setJobTitle(jobTitle[0].text)}
            error={errors?.["positions[0][job_title]"]}
          />
        )}

        <div className={css.center} style={{ marginTop: 50 }}>
          <Button text={t("translation:continue")} onClick={next} qaId="Onboarding.DetailsStep.next" />
        </div>
      </OnboardingBox>
    </LandingLayoutWithoutSidebar>
  );
};

export default DetailsStep;
